import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as ClockIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoCameraIcon,
  WbSunny as WbSunnyIcon,
  EventNote as EventNoteIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

// RTK Query hooks
import {
  useGetMyAttendanceQuery,
  useClockInMutation,
  useClockOutMutation,
  useGetAttendanceHistoryQuery,
  useGetAttendanceStatsQuery,
} from '../services/attendanceApi';
import { useGetShiftsQuery } from '../services/shiftApi';
import { useGetMyProfileQuery } from '../services/employeeApi';
import { useGetMyCompanyQuery } from '../services/companyApi';
import {
  useGetLeaveRequestsQuery,
  useGetLeaveBalancesQuery,
  useGetLeaveTypesQuery,
  useCreateLeaveRequestMutation,
} from '../services/leaveApi';

// Components
import AttendanceCamera from '../components/AttendanceCamera';
import { useGetDepartmentsQuery } from '../services/departmentApi';
import { useGetDesignationsQuery } from '../services/designationApi';

const EmployeePortal = () => {
  const { t } = useTranslation(['common', 'attendance', 'employee']);
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
     // Get departments for employee details
    const { data: departmentsData, error: departmentsError, isLoading: departmentsLoading } = useGetDepartmentsQuery({
      companyId: user?.companyId,
      limit: 1000 // Get all departments for dropdown
    });
  
    // Get designations for employee details
    const { data: designationsData, error: designationsError, isLoading: designationsLoading } = useGetDesignationsQuery({
      companyId: user?.companyId,
      limit: 1000 // Get all designations for dropdown
    });
  // State management
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  const [clockMode, setClockMode] = useState('clockIn'); // 'clockIn' or 'clockOut'
  const [currentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState('');

  // Leave management state
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    reason: '',
    contactDuringLeave: {
      name: '',
      phone: '',
      address: ''
    }
  });

  // Get user's current location if geofencing is enabled
  const getCurrentLocation = async (manageLoadingState = true) => {
    console.log('getCurrentLocation called');
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      setLocationError('Geolocation is not supported by this browser');
      return false;
    }

    if (manageLoadingState) {
      setIsGettingLocation(true);
    }
    setLocationError(null);

    try {
      console.log('Requesting geolocation...');
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      });

      const locationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      console.log('Location captured successfully:', locationData);
      setLocation(locationData);
      setLocationError(null);
      return true;
    } catch (error) {
      console.error('Location error:', error);
      let errorMessage = 'Unable to get location';
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location access denied. Please enable location permissions.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out.';
          break;
        default:
          errorMessage = 'Unable to get location';
          break;
      }
      setLocationError(errorMessage);
      return false;
    } finally {
      if (manageLoadingState) {
        setIsGettingLocation(false);
      }
    }
  };

  // Check if geofencing is enabled
  const isGeofencingEnabled = () => {
    const enabled = company?.settings?.attendance?.geoFencing === true;
    console.log('isGeofencingEnabled check:', {
      company: !!company,
      settings: company?.settings,
      attendance: company?.settings?.attendance,
      geoFencing: company?.settings?.attendance?.geoFencing,
      result: enabled
    });
    return enabled;
  };

  // RTK Query hooks
  const { data: profileData } = useGetMyProfileQuery();
  const { data: companyData, isLoading: companyLoading, error: companyError } = useGetMyCompanyQuery();
  const { data: todayAttendance, refetch: refetchAttendance } = useGetMyAttendanceQuery();
  const { data: historyData, isLoading: historyLoading } = useGetAttendanceHistoryQuery({
    limit: 10,
    sort: '-date'
  });
  const { data: attendanceStats, isLoading: statsLoading } = useGetAttendanceStatsQuery({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const { data: shiftsData } = useGetShiftsQuery({
    companyId: user?.companyId,
    limit: 1000,
    isActive: true
  });

  // Extract data
  const profile = profileData?.data;
  const company = companyData?.data;

  // Leave-related hooks
  const { data: leaveRequestsData, isLoading: leaveRequestsLoading } = useGetLeaveRequestsQuery({
    employeeId: profile?._id,
    limit: 5,
    sort: '-createdAt'
  }, { skip: !profile?._id });
  const { data: leaveBalancesData, isLoading: leaveBalancesLoading } = useGetLeaveBalancesQuery({
    employeeId: profile?._id,
    year: new Date().getFullYear()
  }, { skip: !profile?._id });
  const { data: leaveTypesData } = useGetLeaveTypesQuery();

  // Mutations
  const [clockIn, { isLoading: clockInLoading }] = useClockInMutation();
  const [clockOut, { isLoading: clockOutLoading }] = useClockOutMutation();
  const [createLeaveRequest, { isLoading: creatingLeave }] = useCreateLeaveRequestMutation();

  const todayRecords = Array.isArray(todayAttendance?.data) ? todayAttendance.data : [];
  const attendanceHistory = historyData?.data || [];
  const shifts = shiftsData?.data || [];

  const profileShiftIds = Array.isArray(profile?.shiftIds) && profile.shiftIds.length > 0
    ? profile.shiftIds
    : (profile?.shiftId ? [profile.shiftId] : []);

  const resolveShiftId = (shiftValue) => {
    if (!shiftValue) return '';
    if (typeof shiftValue === 'object') {
      return shiftValue._id || shiftValue.id || '';
    }
    return shiftValue;
  };

  const findShiftById = (shiftValue) => {
    const targetId = resolveShiftId(shiftValue);
    if (!targetId) return null;
    return shifts.find(shift => shift._id === targetId) || null;
  };

  const getShiftWindow = (shift, now) => {
    if (!shift?.startTime || !shift?.endTime) return null;

    const [startHours, startMinutes] = shift.startTime.split(':').map(Number);
    const [endHours, endMinutes] = shift.endTime.split(':').map(Number);

    const start = new Date(now);
    start.setHours(startHours, startMinutes, 0, 0);

    const end = new Date(now);
    end.setHours(endHours, endMinutes, 0, 0);

    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    const bufferBefore = shift.bufferTime?.before ?? 0;
    const bufferAfter = shift.bufferTime?.after ?? 0;

    const windowStart = new Date(start.getTime() - (bufferBefore * 60 * 1000));
    const windowEnd = new Date(end.getTime() + (bufferAfter * 60 * 1000));

    return { windowStart, windowEnd };
  };

  const findMatchingShiftId = () => {
    const now = new Date();
    for (const shiftId of profileShiftIds) {
      const shift = findShiftById(shiftId);
      const window = getShiftWindow(shift, now);
      if (!window) continue;

      if (now >= window.windowStart && now <= window.windowEnd) {
        return shiftId;
      }
    }

    return profileShiftIds[0] || '';
  };

  useEffect(() => {
    if (profileShiftIds.length === 0) return;

    if (!selectedShiftId || !profileShiftIds.includes(selectedShiftId)) {
      setSelectedShiftId(findMatchingShiftId());
    }
  }, [profileShiftIds, selectedShiftId, shifts]);

  const todayRecord = selectedShiftId
    ? todayRecords.find(record => resolveShiftId(record.shiftId) === resolveShiftId(selectedShiftId))
    : todayRecords[0];

  const selectedShift = selectedShiftId
    ? findShiftById(selectedShiftId)
    : findShiftById(todayRecord?.shiftId);

  // Leave data
  const leaveRequests = leaveRequestsData?.data?.leaveRequests || [];
  const leaveBalances = leaveBalancesData?.data?.balances || [];
  const leaveTypes = leaveTypesData?.data?.leaveTypes || [];

  // Company settings
  const attendanceType = company?.settings?.attendance?.type || 'system';
  const selfieRequired = attendanceType === 'selfie';

  // Get location on mount if geofencing is enabled
  useEffect(() => {
    console.log('useEffect triggered - company:', company);
    console.log('Company data structure:', companyData);
    console.log('Geofencing check:', isGeofencingEnabled());
    if (company && isGeofencingEnabled()) {
      console.log('Getting initial location...');
      getCurrentLocation(true);
    }
  }, [company]);

  // Debug company data
  console.log('Company data debug:', {
    companyData,
    companyLoading,
    companyError,
    company: companyData?.data,
    user: user,
    isAuthenticated: user ? true : false,
    currentPath: window.location.pathname,
    userRole: user?.role
  });

  // Debug profile data structure
  console.log('Profile data debug:', {
    profileData,
    profile: profileData?.data,
    department: profileData?.data?.department,
    designation: profileData?.data?.designation,
    departmentType: typeof profileData?.data?.department,
    designationType: typeof profileData?.data?.designation
  });

  // If not authenticated, show login message
  if (!user) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h6">Authentication Required</Typography>
          <Typography>Please log in to access the Employee Portal.</Typography>
        </Alert>
      </Container>
    );
  }

  // If company data is loading, show loading
  if (companyLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading company data...</Typography>
        </Box>
      </Container>
    );
  }

  // If there's an error loading company data
  if (companyError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Company Data</Typography>
          <Typography>{companyError?.data?.message || 'Failed to load company information. Please try refreshing the page.'}</Typography>
        </Alert>
      </Container>
    );
  }

  // Attendance stats
  const stats = attendanceStats?.data || {
    attendanceRate: 0,
    totalDays: 0,
    presentDays: 0,
    absentDays: 0,
    lateDays: 0,
    halfDays: 0,
    overtimeDays: 0,
    workingDays: 0
  };

  // Handle clock in/out
  // Handle clock in/out button click
  const handleClockButtonClick = (mode) => {
    setClockMode(mode);
    
    // If selfie is required, open the AttendanceCamera directly
    if (selfieRequired) {
      setClockDialogOpen(true); // This will show the AttendanceCamera
    } else {
      // For non-selfie attendance, use the existing flow
      setClockDialogOpen(true);
    }
  };

  const handleClockAction = async (mode) => {
    try {
      console.log('Clock action triggered:', mode);
      console.log('Geofencing enabled:', isGeofencingEnabled());
      console.log('Current location state:', location);
      console.log('Location error state:', locationError);

      if (selectedShiftId) {
        const shift = findShiftById(selectedShiftId);
        const window = getShiftWindow(shift, new Date());
        if (window) {
          const now = new Date();
          if (now < window.windowStart || now > window.windowEnd) {
            alert('Attendance is only allowed within the assigned shift window.');
            return;
          }
        }
      }

      const attendanceData = {
        type: mode,
        timestamp: new Date().toISOString(),
        employeeId: profile?._id || profile?.id || profile?.employeeId,
        shiftId: selectedShiftId || undefined,
      };

      // Handle location data for geofencing
      if (isGeofencingEnabled()) {
        let currentLocation = location;

        // If we don't have location yet, try to get it
        if (!currentLocation) {
          console.log('No location available, getting fresh location...');
          setIsGettingLocation(true);

          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
              });
            });

            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };

            console.log('Location captured successfully:', locationData);
            setLocation(locationData);
            currentLocation = locationData;
          } catch (error) {
            console.error('Location error:', error);
            setIsGettingLocation(false);
            let errorMessage = 'Unable to get location';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location access denied. Please enable location permissions.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable.';
                break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out.';
                break;
              default:
                errorMessage = 'Unable to get location';
                break;
            }
            setLocationError(errorMessage);
            alert(`${errorMessage} Please enable location permissions and try again.`);
            return;
          } finally {
            setIsGettingLocation(false);
          }
        } else {
          console.log('Using existing location:', currentLocation);
        }

        // Ensure we have location data
        if (currentLocation && currentLocation.latitude && currentLocation.longitude) {
          attendanceData.location = {
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            accuracy: currentLocation.accuracy
          };
          console.log('Final attendance data with location:', attendanceData);
        } else {
          console.error('No valid location data available');
          alert('Unable to get your location. Please enable location permissions and try again.');
          return;
        }
      }

      if (selfieRequired) {
        // Handle selfie capture if required
        attendanceData.selfie = 'captured'; // This would be actual selfie data
      }

      console.log('Sending attendance data:', attendanceData);

      // Log the final request data
      console.log('=== FINAL REQUEST DATA ===');
      console.log('Mode:', mode);
      console.log('Geofencing enabled:', isGeofencingEnabled());
      console.log('Location included:', !!attendanceData.location);
      console.log('Location data:', attendanceData.location);
      console.log('Full attendance data:', JSON.stringify(attendanceData, null, 2));

      if (mode === 'clockIn') {
        console.log('Calling clockIn API...');
        const result = await clockIn(attendanceData).unwrap();
        console.log('Clock-in API response:', result);
      } else {
        console.log('Calling clockOut API...');
        const result = await clockOut(attendanceData).unwrap();
        console.log('Clock-out API response:', result);
      }

      refetchAttendance();
      setClockDialogOpen(false);
    } catch (error) {
      console.error('Clock action failed:', error);
      setIsGettingLocation(false);

      // Handle geolocation validation errors
      if (error?.data?.message?.includes('meters away')) {
        alert(`Location validation failed: ${error.data.message}`);
      } else {
        alert(error?.data?.message || `Failed to ${mode}`);
      }
    }
  };

  // Get current status
  const getCurrentStatus = () => {
    if (!todayRecord) return { status: 'not_clocked_in', text: 'Not Clocked In', color: 'error' };

    if (todayRecord.actualOutTime) {
      return { status: 'completed', text: 'Day Completed', color: 'success' };
    } else if (todayRecord.actualInTime) {
      return { status: 'clocked_in', text: 'Clocked In', color: 'primary' };
    }

    return { status: 'not_clocked_in', text: 'Not Clocked In', color: 'error' };
  };

  const currentStatus = getCurrentStatus();

  // Format time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return 'Invalid Date';

    // Handle dd/mm/yyyy format from server
    if (typeof date === 'string' && date.includes('/')) {
      // Already in dd/mm/yyyy format, return as is
      return date;
    }

    // Handle other date formats - convert to dd/mm/yyyy
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid Date';

      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.warn('Invalid date format:', date);
      return 'Invalid Date';
    }
  };

  // Leave management handlers
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      const leaveData = {
        ...leaveForm,
        employeeId: profile?._id,
        contactDuringLeave: {
          ...leaveForm.contactDuringLeave,
          name: leaveForm.contactDuringLeave.name || profile?.firstName + ' ' + profile?.lastName,
          phone: leaveForm.contactDuringLeave.phone || profile?.phone,
        }
      };

      await createLeaveRequest(leaveData).unwrap();
      setLeaveDialogOpen(false);
      setLeaveForm({
        leaveTypeId: '',
        fromDate: '',
        toDate: '',
        reason: '',
        contactDuringLeave: {
          name: '',
          phone: '',
          address: ''
        }
      });
      alert('Leave request submitted successfully!');
    } catch (error) {
      alert(error?.data?.message || 'Failed to submit leave request');
    }
  };

  const getLeaveStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };



    const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    if (typeof departmentId === 'object') {
      return departmentId?.name || departmentId?.departmentName || 'N/A';
    }
    // Find department by ID
    const department = departmentsData?.data?.find(d => d._id === departmentId);
    return department?.name || department?.departmentName || 'N/A';
  };

  const getDesignationName = (designationId) => {
    if (!designationId) return 'N/A';
    if (typeof designationId === 'object') {
      return designationId?.name || designationId?.designationName || 'N/A';
    }
    // Find designation by ID
    const designation = designationsData?.data?.find(d => d._id === designationId);
    return designation?.name || designation?.designationName || 'N/A';
  };

  return (
    <>
      <Helmet>
        <title>{`${t('employee:portal.title', 'Employee Portal')} | ${company?.name || 'IAR HRMS'}`}</title>
      </Helmet>

      {/* Modern Background with Gradient */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          py: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Modern Header */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                      fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                    }}
                  >
                    {t('employee:portal.welcome', 'Welcome Back!')}
                  </Typography>
                  {company?.name && (
                    <Typography
                      variant="h4"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        background: 'linear-gradient(45deg, #f093fb, #f5576c)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                        fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                        textAlign: 'center',
                      }}
                    >
                      {company.name}
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                    }}
                  >
                    {currentTime.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      mt: 0.5,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    {currentTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={() => window.location.reload()}
                    sx={{
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(102, 126, 234, 0.2)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <RefreshIcon sx={{ color: 'primary.main' }} />
                  </IconButton>
                  <Chip
                    icon={<WbSunnyIcon />}
                    label={currentStatus.text}
                    color={currentStatus.color}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      px: 2,
                      py: 1,
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Box>

        <Grid container spacing={3}>
          {/* Employee Profile Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c, #4facfe)',
                  backgroundSize: '200% 100%',
                  animation: 'gradientFlow 3s ease-in-out infinite',
                },
                '@keyframes gradientFlow': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' },
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
                <Box sx={{ position: 'relative', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: { xs: 70, md: 90 },
                      height: { xs: 70, md: 90 },
                      mx: 'auto',
                      mb: 2,
                      bgcolor: 'linear-gradient(135deg, #667eea, #764ba2)',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      fontWeight: 600,
                      border: '3px solid rgba(255, 255, 255, 0.8)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                      },
                    }}
                  >
                    {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 'calc(50% - 35px)',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: currentStatus.color === 'success' ? '#4caf50' :
                              currentStatus.color === 'warning' ? '#ff9800' : '#f44336',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {profile?.firstName} {profile?.lastName}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  {getDesignationName(profile?.designation)}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 3,
                    opacity: 0.8,
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                  }}
                >
                  {
                   getDepartmentName(profile?.department)
                  }
                </Typography>

                <Chip
                  label={currentStatus.text}
                  color={currentStatus.color}
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    px: 2,
                    py: 1,
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Clock In/Out Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                >
                  <ClockIcon sx={{ fontSize: 36, color: 'white' }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: 'text.primary',
                  }}
                >
                  {t('attendance:clock.title', 'Time Clock')}
                </Typography>

                {/* Time Display */}
                <Box sx={{ mb: 3 }}>
                  {todayRecord?.actualInTime && (
                    <Box
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {t('attendance:clock.clockIn', 'Clocked In')}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        {formatTime(todayRecord.actualInTime)}
                      </Typography>
                    </Box>
                  )}

                  {todayRecord?.actualOutTime && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'success.main',
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {t('attendance:clock.clockOut', 'Clocked Out')}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'success.main',
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        {formatTime(todayRecord.actualOutTime)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Location Status for Geofencing */}
                {isGeofencingEnabled() && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: location ? 'success.main' : locationError ? 'error.main' : 'warning.main',
                      bgcolor: location ? 'rgba(76, 175, 80, 0.1)' : locationError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        fontWeight: 500,
                        color: location ? 'success.main' : locationError ? 'error.main' : 'warning.main',
                      }}
                    >
                      <LocationIcon fontSize="small" />
                      📍 {t('attendance:clock.locationStatus', 'Location Status')}
                    </Typography>

                    {isGettingLocation ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          {t('common:gettingLocation', 'Getting your location...')}
                        </Typography>
                      </Box>
                    ) : location ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          ✅ {t('attendance:clock.locationReady', 'Location ready')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            mt: 0.5,
                            fontFamily: 'monospace',
                          }}
                        >
                          📍 Lat: {location.latitude.toFixed(6)}, Lon: {location.longitude.toFixed(6)}
                          {location.accuracy && ` (±${Math.round(location.accuracy)}m)`}
                        </Typography>
                      </Box>
                    ) : locationError ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'error.main',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          ❌ {t('attendance:clock.locationFailed', 'Location failed')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            color: 'error.main',
                            mt: 0.5,
                          }}
                        >
                          {locationError}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => getCurrentLocation(true)}
                          sx={{
                            mt: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                          }}
                        >
                          {t('common:retry', 'Retry')}
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'warning.main',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          📍 {t('attendance:clock.locationRequired', 'Location required for attendance')}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => getCurrentLocation(true)}
                          sx={{
                            mt: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                          }}
                        >
                          {t('common:getLocation', 'Get Location')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {profileShiftIds.length > 1 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        mb: 1
                      }}
                    >
                      {t('common:shift', 'Shift')}
                    </Typography>
                    <Box
                      component="select"
                      value={selectedShiftId}
                      onChange={(e) => setSelectedShiftId(e.target.value)}
                      sx={{
                        width: '100%',
                        px: 2,
                        py: 1.2,
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        background: 'white',
                        fontSize: '0.95rem'
                      }}
                    >
                      {profileShiftIds.map(shiftId => {
                        const shift = findShiftById(shiftId);
                        const label = shift
                          ? `${shift.name} (${shift.startTime} - ${shift.endTime})`
                          : `Shift ${shiftId}`;
                        return (
                          <option key={shiftId} value={shiftId}>
                            {label}
                          </option>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {todayRecord?.status === 'half_day' && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600
                      }}
                    >
                      {t('attendance:halfDay', 'Half Day')} {selectedShift?.halfDayThreshold ? `(${selectedShift.halfDayThreshold}h rule)` : ''}
                    </Typography>
                  </Box>
                )}

                {/* Clock Buttons */}
                <Box sx={{ mt: 3 }}>
                  {currentStatus.status === 'not_clocked_in' && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => {
                        handleClockButtonClick('clockIn');
                        if (isGeofencingEnabled() && !location && !isGettingLocation) {
                          console.log('Opening clock-in dialog, getting location...');
                          getCurrentLocation(true);
                        }
                      }}
                      sx={{
                        minWidth: 140,
                        py: 1.5,
                        px: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                        },
                      }}
                    >
                      {t('attendance:clock.clockIn', 'Clock In')}
                    </Button>
                  )}

                  {currentStatus.status === 'clocked_in' && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<StopIcon />}
                      onClick={() => {
                        handleClockButtonClick('clockOut');
                        if (isGeofencingEnabled() && !location && !isGettingLocation) {
                          console.log('Opening clock-out dialog, getting location...');
                          getCurrentLocation(true);
                        }
                      }}
                      sx={{
                        minWidth: 140,
                        py: 1.5,
                        px: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        boxShadow: '0 4px 16px rgba(255, 107, 107, 0.3)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ff5252, #d84315)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(255, 107, 107, 0.4)',
                        },
                      }}
                    >
                      {t('attendance:clock.clockOut', 'Clock Out')}
                    </Button>
                  )}

                  {currentStatus.status === 'completed' && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'success.main',
                          fontWeight: 600,
                        }}
                      >
                        {t('attendance:clock.dayCompleted', 'Day Completed')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Attendance Stats Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <CalendarIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: 'text.primary',
                    }}
                  >
                    {t('attendance:stats.title', 'This Month')}
                  </Typography>
                </Box>

                {/* Attendance Rate Progress */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {t('attendance:stats.attendanceRate', 'Attendance Rate')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      {stats.attendanceRate}%
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 12,
                      borderRadius: 6,
                      background: 'rgba(102, 126, 234, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: `${stats.attendanceRate}%`,
                        background: 'linear-gradient(90deg, #667eea, #764ba2)',
                        borderRadius: 6,
                        transition: 'width 1s ease-in-out',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mt: 1,
                      fontSize: '0.8rem',
                    }}
                  >
                    {stats.presentDays + (stats.halfDays * 0.5)}/{stats.workingDays} {t('attendance:stats.days', 'days')}
                    {stats.halfDays > 0 && (
                      <span style={{ fontSize: '0.7rem', color: '#757575', display: 'block' }}>
                        ({stats.halfDays} half days)
                      </span>
                    )}
                  </Typography>
                </Box>

                {/* Stats Grid */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                        mb: 0.5,
                      }}
                    >
                      {stats.presentDays}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}
                    >
                      {t('attendance:stats.present', 'Present')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))',
                      border: '1px solid rgba(76, 175, 80, 0.2)',
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'warning.main',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                        mb: 0.5,
                      }}
                    >
                      {stats.halfDays}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}
                    >
                      {t('attendance:stats.halfDays', 'Half Days')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(229, 57, 53, 0.1))',
                      border: '1px solid rgba(244, 67, 54, 0.2)',
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(244, 67, 54, 0.2)',
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'error.main',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                        mb: 0.5,
                      }}
                    >
                      {stats.absentDays}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}
                    >
                      {t('attendance:stats.absent', 'Absent')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Employee Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: 'text.primary',
                    }}
                  >
                    {t('employee:profile.title', 'Employee Information')}
                  </Typography>
                </Box>

                <Divider
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent)',
                  }}
                />

                <List dense sx={{ p: 0 }}>
                  {/* Full Name */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Full Name
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.firstName && profile?.lastName
                            ? `${profile.firstName} ${profile.lastName}`
                            : user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : 'Not available'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Employee ID */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(255, 152, 0, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 193, 7, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WorkIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Employee ID
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.employeeId || 'Not assigned'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Designation */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(76, 175, 80, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WorkIcon sx={{ color: 'success.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Designation
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {getDesignationName(profile?.designation) || 'Not assigned'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Department */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(156, 39, 176, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(186, 104, 200, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BusinessIcon sx={{ color: 'secondary.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Department
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {getDepartmentName(profile?.department) || 'Not assigned'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Branch/Work Location */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(244, 67, 54, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(229, 115, 115, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LocationIcon sx={{ color: 'error.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Work Location
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.branch?.name || company?.name || 'Head Office'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Joining Date */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(0, 188, 212, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.1), rgba(77, 208, 225, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CalendarIcon sx={{ color: 'info.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Joining Date
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.joiningDate ? formatDate(profile.joiningDate) : 'Not available'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Employment Type */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(255, 87, 34, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.1), rgba(255, 138, 101, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WorkIcon sx={{ color: 'orange', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Employment Type
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.employmentType || 'Full-time'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Contact Information */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(76, 175, 80, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PhoneIcon sx={{ color: 'success.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Phone
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.phone || 'Not provided'}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {/* Email */}
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EmailIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          Email
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {profile?.email || user?.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Attendance History */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    <HistoryIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: 'text.primary',
                    }}
                  >
                    {t('attendance:history.recent', 'Recent Attendance')}
                  </Typography>
                </Box>

                <Divider
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent)',
                  }}
                />

                {historyLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 6,
                    }}
                  >
                    <CircularProgress
                      sx={{
                        color: 'primary.main',
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {t('common:loading', 'Loading...')}
                    </Typography>
                  </Box>
                ) : attendanceHistory.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 6,
                      px: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <HistoryIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {t('attendance:history.noRecords', 'No attendance records found')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        opacity: 0.7,
                      }}
                    >
                      {t('attendance:history.startTracking', 'Your attendance history will appear here')}
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ p: 0 }}>
                    {attendanceHistory.slice(0, 5).map((record, index) => {
                      // Determine status and colors
                      const getStatusInfo = (record) => {
                        if (record.status === 'present') {
                          return {
                            color: 'success.main',
                            bgColor: 'rgba(76, 175, 80, 0.1)',
                            borderColor: 'rgba(76, 175, 80, 0.3)',
                            label: 'Present',
                            icon: CheckCircleIcon
                          };
                        } else if (record.status === 'absent') {
                          return {
                            color: 'error.main',
                            bgColor: 'rgba(244, 67, 54, 0.1)',
                            borderColor: 'rgba(244, 67, 54, 0.3)',
                            label: 'Absent',
                            icon: XCircleIcon
                          };
                        } else if (record.status === 'late') {
                          return {
                            color: 'warning.main',
                            bgColor: 'rgba(255, 152, 0, 0.1)',
                            borderColor: 'rgba(255, 152, 0, 0.3)',
                            label: 'Late',
                            icon: ScheduleIcon
                          };
                        } else if (record.status === 'half_day') {
                          return {
                            color: 'info.main',
                            bgColor: 'rgba(33, 150, 243, 0.1)',
                            borderColor: 'rgba(33, 150, 243, 0.3)',
                            label: 'Half Day',
                            icon: ScheduleIcon
                          };
                        }
                        return {
                          color: 'text.secondary',
                          bgColor: 'rgba(158, 158, 158, 0.1)',
                          borderColor: 'rgba(158, 158, 158, 0.3)',
                          label: 'Unknown',
                          icon: ScheduleIcon
                        };
                      };

                      const statusInfo = getStatusInfo(record);
                      const StatusIcon = statusInfo.icon;
                      
                      // Calculate work duration if both times are available
                      const workDuration = record.actualInTime && record.actualOutTime 
                        ? (() => {
                            const inTime = new Date(record.actualInTime);
                            const outTime = new Date(record.actualOutTime);
                            const diffMs = outTime - inTime;
                            const hours = Math.floor(diffMs / (1000 * 60 * 60));
                            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                            return `${hours}h ${minutes}m`;
                          })()
                        : null;

                      return (
                        <ListItem
                          key={`${record.date}-${index}`}
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              background: statusInfo.bgColor,
                              transform: 'translateX(4px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            },
                            border: '1px solid',
                            borderColor: statusInfo.borderColor,
                            background: statusInfo.bgColor,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: statusInfo.bgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${statusInfo.borderColor}`,
                              }}
                            >
                              <StatusIcon
                                sx={{
                                  color: statusInfo.color,
                                  fontSize: 16,
                                }}
                              />
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  {formatDate(record.date)}
                                </Typography>
                                <Chip
                                  label={statusInfo.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusInfo.bgColor,
                                    color: statusInfo.color,
                                    border: `1px solid ${statusInfo.borderColor}`,
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    mb: 0.5,
                                  }}
                                >
                                  {record.actualInTime ? `In: ${formatTime(record.actualInTime)}` : 'Not clocked in'}
                                  {record.actualOutTime && ` • Out: ${formatTime(record.actualOutTime)}`}
                                </Typography>
                                {workDuration && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: statusInfo.color,
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Duration: {workDuration}
                                  </Typography>
                                )}
                                {record.lateMinutes > 0 && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'warning.main',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Late by {record.lateMinutes} minutes
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Leave Management Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                      }}
                    >
                      <EventNoteIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        color: 'text.primary',
                      }}
                    >
                      Leave Management
                    </Typography>
                  </Box>
                  {/* <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setLeaveDialogOpen(true)}
                    sx={{
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ff5252, #d84315)',
                      },
                    }}
                  >
                    Request Leave
                  </Button> */}
                </Box>

                <Divider
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(90deg, transparent, rgba(255, 107, 107, 0.3), transparent)',
                  }}
                />

                {/* Leave Balances */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 16 }} />
                    Leave Balances
                  </Typography>
                  {leaveBalancesLoading ? (
                    <CircularProgress size={20} />
                  ) : leaveBalances.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No leave balances available
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {leaveBalances.slice(0, 3).map((balance) => (
                        <Chip
                          key={balance._id}
                          label={`${balance.leaveTypeId?.name || 'Unknown'}: ${balance.remainingBalance || 0}`}
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(238, 90, 36, 0.1))',
                            border: '1px solid rgba(255, 107, 107, 0.2)',
                            color: 'text.primary',
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Recent Leave Requests */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                    Recent Requests
                  </Typography>
                  {leaveRequestsLoading ? (
                    <CircularProgress size={20} />
                  ) : leaveRequests.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No leave requests found
                    </Typography>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {leaveRequests.slice(0, 3).map((request) => (
                        <ListItem
                          key={request._id}
                          sx={{
                            px: 0,
                            py: 1,
                            borderRadius: 1,
                            '&:hover': {
                              background: 'rgba(255, 107, 107, 0.05)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {request.leaveTypeId?.name || 'Unknown Leave Type'}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {formatDate(request.fromDate)} - {formatDate(request.toDate)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={request.status}
                                  color={getLeaveStatusColor(request.status)}
                                  sx={{
                                    height: 16,
                                    fontSize: '0.625rem',
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      
        {/* Clock In/Out Dialog */}
        {selfieRequired ? (
          <AttendanceCamera
            isOpen={clockDialogOpen}
            onClose={() => setClockDialogOpen(false)}
            mode={clockMode}
            onSuccess={() => refetchAttendance()}
          />
        ) : (
          <Dialog
            open={clockDialogOpen}
            onClose={() => setClockDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
          <DialogTitle
            sx={{
              textAlign: 'center',
              pb: 1,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              borderRadius: '4px 4px 0 0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {clockMode === 'clockIn' ? (
                  <PlayArrowIcon sx={{ fontSize: 24, color: 'white' }} />
                ) : (
                  <StopIcon sx={{ fontSize: 24, color: 'white' }} />
                )}
              </Box>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.8rem' },
              }}
            >
              {clockMode === 'clockIn'
                ? t('attendance:clock.clockInTitle', 'Clock In')
                : t('attendance:clock.clockOutTitle', 'Clock Out')
              }
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                mb: 3,
                color: 'text.secondary',
                fontSize: { xs: '1rem', md: '1.1rem' },
              }}
            >
              {clockMode === 'clockIn'
                ? t('attendance:clock.clockInMessage', 'Are you ready to clock in for today?')
                : t('attendance:clock.clockOutMessage', 'Are you ready to clock out?')
              }
            </Typography>

            <Box
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {t('attendance:clock.currentTime', 'Current Time')}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  fontFamily: 'monospace',
                }}
              >
                {currentTime.toLocaleTimeString()}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  fontSize: '0.9rem',
                }}
              >
                {currentTime.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>

            {isGeofencingEnabled() && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: locationError
                    ? 'error.main'
                    : isGettingLocation
                    ? 'warning.main'
                    : location
                    ? 'success.main'
                    : 'warning.main',
                  background: locationError
                    ? 'rgba(244, 67, 54, 0.1)'
                    : isGettingLocation
                    ? 'rgba(255, 152, 0, 0.1)'
                    : location
                    ? 'rgba(76, 175, 80, 0.1)'
                    : 'rgba(255, 152, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: locationError
                        ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.2), rgba(229, 115, 115, 0.2))'
                        : isGettingLocation
                        ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 193, 7, 0.2))'
                        : location
                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(139, 195, 74, 0.2))'
                        : 'linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 193, 7, 0.2))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationIcon
                      sx={{
                        color: locationError
                          ? 'error.main'
                          : isGettingLocation
                          ? 'warning.main'
                          : location
                          ? 'success.main'
                          : 'warning.main',
                        fontSize: 16,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: locationError
                        ? 'error.main'
                        : isGettingLocation
                        ? 'warning.main'
                        : location
                        ? 'success.main'
                        : 'warning.main',
                      fontWeight: 600,
                    }}
                  >
                    {t('attendance:clock.locationStatus', 'Location Status')}
                  </Typography>
                </Box>

                <Box>
                  {isGettingLocation ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {t('common:gettingLocation', 'Getting your location...')}
                      </Typography>
                    </Box>
                  ) : locationError ? (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'error.main',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        ❌ {locationError}
                      </Typography>
                    </Box>
                  ) : location ? (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'success.main',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        ✅ {t('attendance:clock.locationReady', 'Location ready')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          fontFamily: 'monospace',
                        }}
                      >
                        📍 Lat: {location.latitude.toFixed(6)}, Lon: {location.longitude.toFixed(6)}
                        {location.accuracy && ` (±${Math.round(location.accuracy)}m)`}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'warning.main',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      📍 {t('attendance:clock.locationRequired', 'Location will be obtained when you clock in/out')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              p: { xs: 3, md: 4 },
              pt: 0,
              gap: 2,
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={() => setClockDialogOpen(false)}
              variant="outlined"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '1rem' },
                borderColor: 'rgba(102, 126, 234, 0.3)',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  background: 'rgba(102, 126, 234, 0.05)',
                },
              }}
            >
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => handleClockAction(clockMode)}
              variant="contained"
              disabled={clockInLoading || clockOutLoading || isGettingLocation}
              startIcon={
                isGettingLocation ? (
                  <CircularProgress size={16} />
                ) : clockMode === 'clockIn' ? (
                  <PlayArrowIcon />
                ) : (
                  <StopIcon />
                )
              }
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '1rem' },
                background: clockMode === 'clockIn'
                  ? 'linear-gradient(135deg, #667eea, #764ba2)'
                  : 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  background: clockMode === 'clockIn'
                    ? 'linear-gradient(135deg, #5a6fd8, #6a4190)'
                    : 'linear-gradient(135deg, #ff5252, #d84315)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              {isGettingLocation ? (
                t('common:gettingLocation', 'Getting Location...')
              ) : clockInLoading || clockOutLoading ? (
                <CircularProgress size={20} />
              ) : clockMode === 'clockIn' ? (
                t('attendance:clock.clockIn', 'Clock In')
              ) : (
                t('attendance:clock.clockOut', 'Clock Out')
              )}
            </Button>
          </DialogActions>
        </Dialog>
        )}

        {/* Leave Request Dialog */}
        <Dialog
          open={leaveDialogOpen}
          onClose={() => setLeaveDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              pb: 1,
              background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
              color: 'white',
              borderRadius: '4px 4px 0 0',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.8rem' },
              }}
            >
              Request Leave
            </Typography>
          </DialogTitle>

          <form onSubmit={handleLeaveSubmit}>
            <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    Leave Details
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      Leave Type *
                    </Typography>
                    <select
                      value={leaveForm.leaveTypeId}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="">Select Leave Type</option>
                      {leaveTypes.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.name} ({type.code})
                        </option>
                      ))}
                    </select>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      From Date *
                    </Typography>
                    <input
                      type="date"
                      value={leaveForm.fromDate}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, fromDate: e.target.value }))}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      To Date *
                    </Typography>
                    <input
                      type="date"
                      value={leaveForm.toDate}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, toDate: e.target.value }))}
                      required
                      min={leaveForm.fromDate || new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    Additional Information
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      Reason
                    </Typography>
                    <textarea
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Please provide a reason for your leave request..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        resize: 'vertical',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    Emergency Contact
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      Contact Name
                    </Typography>
                    <input
                      type="text"
                      value={leaveForm.contactDuringLeave.name}
                      onChange={(e) => setLeaveForm(prev => ({
                        ...prev,
                        contactDuringLeave: {
                          ...prev.contactDuringLeave,
                          name: e.target.value
                        }
                      }))}
                      placeholder="Emergency contact name"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      Contact Phone
                    </Typography>
                    <input
                      type="tel"
                      value={leaveForm.contactDuringLeave.phone}
                      onChange={(e) => setLeaveForm(prev => ({
                        ...prev,
                        contactDuringLeave: {
                          ...prev.contactDuringLeave,
                          phone: e.target.value
                        }
                      }))}
                      placeholder="Emergency contact phone"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: { xs: 3, md: 4 }, pt: 0 }}>
              <Button
                onClick={() => setLeaveDialogOpen(false)}
                sx={{
                  mr: 2,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={creatingLeave}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff5252, #d84315)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {creatingLeave ? (
                  <CircularProgress size={20} />
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
      </Box>
    </>
  );
};

export default EmployeePortal;
